import React, { useEffect, useState } from 'react';
import { AppBar, Toolbar, Box, IconButton, Typography, Chip, Badge, Menu, MenuItem, Button } from '@mui/material';
import { AccountCircle, NotificationsNone } from '@mui/icons-material';
import { authService } from '../services/authService';
import { TaskNotificationItem, taskNotificationApiService } from '../services/taskNotificationApiService';

interface TopBarProps {
  onAuthClick: () => void;
}

const TopBar: React.FC<TopBarProps> = ({ onAuthClick }) => {
  const [currentUser, setCurrentUser] = useState<{ 
    userId: string; 
    userName: string; 
    email: string; 
    roles: string[] 
  } | null>(null);
  const [notifications, setNotifications] = useState<TaskNotificationItem[]>([]);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState<null | HTMLElement>(null);

  const unreadCount = notifications.filter((item) => !item.isRead).length;

  useEffect(() => {
    const user = authService.getCurrentUser();
    setCurrentUser(user);

    if (!user?.userId) {
      setNotifications([]);
      return;
    }

    let isMounted = true;

    const loadNotifications = async () => {
      try {
        const items = await taskNotificationApiService.getMyNotifications();
        if (isMounted) {
          setNotifications(items);
        }
      } catch {
        if (isMounted) {
          setNotifications([]);
        }
      }
    };

    loadNotifications();
    const intervalId = window.setInterval(loadNotifications, 15000);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  const handleOpenNotifications = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchorEl(event.currentTarget);
    taskNotificationApiService.getMyNotifications().then(setNotifications).catch(() => undefined);
  };

  const handleCloseNotifications = () => {
    setNotificationsAnchorEl(null);
  };

  const handleMarkAllRead = () => {
    taskNotificationApiService
      .markAllAsRead()
      .then(() => taskNotificationApiService.getMyNotifications())
      .then(setNotifications)
      .catch(() => undefined);
  };

  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: '#d7dde4', // light blue-gray
        boxShadow: 'none',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar>
        <Box sx={{ flexGrow: 1 }} />
        
        {currentUser && (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              mr: 1
            }}
          >
            <Box sx={{ textAlign: 'right' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#3a4651',
                  fontWeight: 600,
                  lineHeight: 1.2
                }}
              >
                {currentUser.email}
              </Typography>
              {currentUser.roles && currentUser.roles.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, justifyContent: 'flex-end' }}>
                  {currentUser.roles.map((role, index) => (
                    <Chip
                      key={index}
                      label={role}
                      size="small"
                      sx={{
                        backgroundColor: '#ff6b35',
                        color: 'white',
                        fontSize: '0.65rem',
                        height: '18px',
                        '& .MuiChip-label': {
                          px: 0.75,
                          py: 0
                        }
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        )}

        <IconButton
          size="large"
          onClick={handleOpenNotifications}
          sx={{
            color: '#3a4651',
            '&:hover': {
              backgroundColor: 'rgba(58, 70, 81, 0.1)',
            },
          }}
        >
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsNone />
          </Badge>
        </IconButton>

        <Menu
          anchorEl={notificationsAnchorEl}
          open={Boolean(notificationsAnchorEl)}
          onClose={handleCloseNotifications}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { width: 360, maxHeight: 420 } }}
        >
          <Box sx={{ px: 1.5, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Értesítések</Typography>
            <Button size="small" onClick={handleMarkAllRead}>Mind olvasott</Button>
          </Box>

          {notifications.length === 0 && (
            <MenuItem disabled>Nincs új értesítés</MenuItem>
          )}

          {notifications.slice(0, 20).map((item) => (
            <MenuItem key={item.id} sx={{ alignItems: 'flex-start', whiteSpace: 'normal' }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: item.isRead ? 500 : 700 }}>
                  {item.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                  {item.message}
                </Typography>
                <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.25 }}>
                  {new Date(item.createdAt).toLocaleString('hu-HU')}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Menu>
        
        <IconButton
          size="large"
          onClick={onAuthClick}
          sx={{ 
            color: '#3a4651',
            '&:hover': {
              backgroundColor: 'rgba(58, 70, 81, 0.1)',
            }
          }}
        >
          <AccountCircle />
        </IconButton>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
